import { Table, type TableProps } from 'antd';
import { type Dispatch, type Key, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { type PromoCodeListingDataFragment } from '../../../../api/fragments/PromoCodeListingData';
import { type PromoCodeModuleSpecsFragment } from '../../../../api/fragments/PromoCodeModuleSpecs';
import PaginatedTable, { type PageAction } from '../../../../components/PaginatedTable';
import renderBooleanIcon from '../../../../utilities/renderBooleanIcon';
import useFormatDate from '../../../../utilities/useFormatDate';
import { renderInitialValue } from '../../PromoCodeListing/PromoCodeList';
import { type State } from '../../PromoCodeListing/useListReducer';

export type PromoCodeModuleTableProps = {
    module: PromoCodeModuleSpecsFragment;
    dataSource: PromoCodeListingDataFragment[];
    state: State;
    dispatch: Dispatch<PageAction>;
    total: number;
};

type ModuleDataSource = PromoCodeListingDataFragment & { key: Key };
type PromoCodeTable = TableProps<ModuleDataSource>;

const PromoCodeModuleTable = ({ module, dataSource, dispatch, state, total }: PromoCodeModuleTableProps) => {
    const { t } = useTranslation(['promoCodeModuleDetails', 'promoCodeList']);
    const navigate = useNavigate();
    const formatDate = useFormatDate();

    const onRow = useCallback<PromoCodeTable['onRow']>(
        record => ({
            onClick: () => navigate(`/admin/promoCodes/${record.id}`),
        }),
        [navigate]
    );

    return (
        <PaginatedTable
            dataSource={dataSource}
            dispatch={dispatch}
            onRow={onRow}
            rowKey="id"
            state={state}
            tableName={t('promoCodeList:title')}
            total={total}
        >
            <Table.Column
                key="promoCode"
                dataIndex="promoCode"
                title={t('promoCodeModuleDetails:promoCodeModuleTable.columns.promoCode')}
            />
            <Table.Column
                key="amount"
                dataIndex={['promoType', 'amount']}
                render={(value, record: PromoCodeListingDataFragment) => renderInitialValue(record)}
                title={t('promoCodeModuleDetails:promoCodeModuleTable.columns.promoTypeAmount')}
            />
            <Table.Column
                key="start"
                dataIndex={['period', 'start']}
                render={(value, record: PromoCodeListingDataFragment) => formatDate({ date: value })}
                title={t('promoCodeModuleDetails:promoCodeModuleTable.columns.start')}
            />
            <Table.Column
                key="end"
                dataIndex={['period', 'end']}
                render={(value, record: PromoCodeListingDataFragment) => formatDate({ date: value })}
                title={t('promoCodeModuleDetails:promoCodeModuleTable.columns.end')}
            />
            <Table.Column
                key="isActive"
                align="center"
                dataIndex="isActive"
                render={value => renderBooleanIcon(value, true)}
                title={t('promoCodeModuleDetails:promoCodeModuleTable.columns.isActive')}
                width={120}
            />
        </PaginatedTable>
    );
};

export default PromoCodeModuleTable;
